<script setup>
import { computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const props = defineProps({
  teamList: {
    type: Array,
    default: () => [],
  },
  selectedTeams: {
    type: Array,
    default: () => [],
  },
  updateSelectedTeams: {
    type: Function,
    default: () => {},
  },
  isWorking: {
    type: Boolean,
    default: false,
  },
  submitButtonText: {
    type: String,
    default: '',
  },
  currentBranchId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const branches = computed(() => store.getters['branches/getBranches'] || []);

const getBranchName = branchId => {
  const branch = branches.value.find(b => Number(b.id) === Number(branchId));
  return branch ? branch.name : '';
};

const isTeamDisabled = team => {
  return (
    team.branch_id && Number(team.branch_id) !== Number(props.currentBranchId)
  );
};

const selectedTeamCount = computed(() => props.selectedTeams.length);

const availableTeams = computed(() =>
  props.teamList.filter(teamItem => !isTeamDisabled(teamItem))
);

const allTeamsSelected = computed(
  () =>
    props.selectedTeams.length === availableTeams.value.length &&
    availableTeams.value.length > 0
);

const someTeamsSelected = computed(
  () => props.selectedTeams.length > 0 && !allTeamsSelected.value
);

const disableSubmitButton = computed(
  () => selectedTeamCount.value === 0 && !props.currentBranchId
); // allow submitting 0 if editing

const isTeamSelected = teamId => {
  return props.selectedTeams.includes(teamId);
};

const handleSelectTeam = teamId => {
  const team = props.teamList.find(teamItem => teamItem.id === teamId);
  if (team && isTeamDisabled(team)) return; // Prevent selection if disabled

  const shouldRemove = isTeamSelected(teamId);

  let result = [];
  if (shouldRemove) {
    result = props.selectedTeams.filter(item => item !== teamId);
  } else {
    result = [...props.selectedTeams, teamId];
  }

  props.updateSelectedTeams(result);
};

const toggleSelectAll = () => {
  if (allTeamsSelected.value) {
    props.updateSelectedTeams([]);
  } else {
    const result = availableTeams.value.map(item => item.id);
    props.updateSelectedTeams(result);
  }
};

const headers = computed(() => [
  '',
  t('BRANCHES_SETTINGS.TEAM_SELECTOR.TEAM'),
  t('BRANCHES_SETTINGS.TEAM_SELECTOR.DESCRIPTION'),
  t('BRANCHES_SETTINGS.TEAM_SELECTOR.STATUS'),
]);
</script>

<template>
  <BaseTable :headers="headers" :items="teamList">
    <template #header-0>
      <div class="flex items-center">
        <Checkbox
          :model-value="allTeamsSelected"
          :indeterminate="someTeamsSelected"
          :title="$t('BRANCHES_SETTINGS.TEAM_SELECTOR.SELECT_ALL')"
          @change="toggleSelectAll"
        />
      </div>
    </template>

    <template #row="{ items }">
      <BaseTableRow v-for="team in items" :key="team.id" :item="team">
        <template #default>
          <BaseTableCell class="w-5">
            <div class="flex items-center">
              <Checkbox
                :model-value="isTeamSelected(team.id)"
                :disabled="isTeamDisabled(team)"
                @change="() => handleSelectTeam(team.id)"
              />
            </div>
          </BaseTableCell>

          <BaseTableCell class="min-w-0 max-w-40">
            <div class="flex items-center gap-2 min-w-0">
              <h4
                class="text-heading-3 mb-0 text-n-slate-12 truncate"
                :class="{ 'text-n-slate-11': isTeamDisabled(team) }"
              >
                {{ team.name }}
              </h4>
            </div>
          </BaseTableCell>

          <BaseTableCell class="min-w-0">
            <span
              class="text-body-main text-n-slate-11 truncate block"
              :class="{ 'opacity-50': isTeamDisabled(team) }"
            >
              {{ team.description || '---' }}
            </span>
          </BaseTableCell>

          <BaseTableCell class="min-w-0">
            <span
              v-if="isTeamDisabled(team)"
              class="text-xs px-2 py-1 bg-n-slate-3 text-n-slate-11 rounded font-medium"
            >
              {{
                $t('BRANCHES_SETTINGS.TEAM_SELECTOR.ASSIGNED_TO', {
                  branch: getBranchName(team.branch_id),
                })
              }}
            </span>
            <span
              v-else-if="
                team.branch_id &&
                Number(team.branch_id) === Number(currentBranchId)
              "
              class="text-xs px-2 py-1 bg-n-brand-3 text-n-brand-11 rounded font-medium"
            >
              {{ $t('BRANCHES_SETTINGS.TEAM_SELECTOR.CURRENTLY_ASSIGNED') }}
            </span>
            <span
              v-else
              class="text-xs px-2 py-1 bg-n-green-3 text-n-green-11 rounded font-medium"
            >
              {{ $t('BRANCHES_SETTINGS.TEAM_SELECTOR.AVAILABLE') }}
            </span>
          </BaseTableCell>
        </template>
      </BaseTableRow>
    </template>
  </BaseTable>

  <div class="flex items-center justify-between mt-4">
    <p class="text-body-main text-n-slate-11">
      {{
        $t('BRANCHES_SETTINGS.TEAM_SELECTOR.SELECTED_COUNT', {
          selected: selectedTeams.length,
          total: availableTeams.length,
        })
      }}
    </p>
    <NextButton
      type="submit"
      :label="submitButtonText"
      :disabled="disableSubmitButton"
      :is-loading="isWorking"
    />
  </div>
</template>
